import React from "react";
import { hasValue, isNodeEmpty, transformObjectAsKvps } from "Tools";
import * as Yup from "yup";
import { MixedSchema } from "yup/lib/mixed";
import {
  AnyObject,
  AssertsShape,
  OptionalObjectSchema,
  TypeOfShape,
} from "yup/lib/object";
import { Converter } from "./FormTools";

Yup.setLocale({
  mixed: {
    required: requiredMessage,
  },
  string: {
    required: requiredMessage,
  },
  number: {
    required: requiredMessage,
    max: maxMessage,
  },
  date: {
    required: requiredMessage,
  },
  object: {
    required: requiredMessage,
  },
  array: {
    required: requiredMessage,
  },
  boolean: {
    required: requiredMessage,
  },
});

export function ErrorMessage(props: { children: React.ReactNode }) {
  const errorNodes =
    props.children instanceof Array
      ? [...new Set(props.children)]
      : !isNodeEmpty(props.children)
      ? [props.children]
      : [];
  return (
    <>
      {errorNodes.map((errorNode, i) => (
        <span
          className="help is-danger w-100"
          style={{ textAlign: "unset" }}
          key={i}
        >
          {errorNode}
        </span>
      ))}
    </>
  );
}

function maxMessage(input: { max: number; label?: string }) {
  return `Maximum value for ${input.label} is ${input.max.toLocaleString()}`;
}

function requiredMessage(input: { label?: string }) {
  return `${input.label} is required`;
}

export const numberConverter: Converter<number | null, string | null> = {
  format: v => v?.toString() ?? null,
  parse: v => (hasValue(v) ? Number(v) : null),
};

declare global {
  interface ObjectConstructor {
    entries<T extends Record<string, unknown>>(
      o: T,
    ): [keyof T & string, T[keyof T]][];
  }
}
type UnionSchema<
  TagKey extends string,
  Enum extends string,
  Schemas extends Record<Enum, Record<string, Yup.AnySchema | undefined>>,
  GetSchema extends { [E in Enum]: Yup.AnySchema } = {
    [E in Enum]: Yup.StringSchema<E>;
  },
> = OptionalObjectSchema<
  {
    [E in Enum]: {
      [K in keyof Schemas[Enum]]: Schemas[E][K] extends undefined
        ? MixedSchema<Yup.TypeOf<NonNullable<Schemas[Enum][K]>>>
        : Schemas[E][K];
    } & Record<TagKey, GetSchema[E]>;
  }[Enum]
>;

export const YupDefined = {
  string: () => Yup.string() as Yup.StringSchema<string>,
  number: () => Yup.number() as Yup.NumberSchema<number>,
  date: () => Yup.date() as Yup.DateSchema<Date>,
  boolean: () => Yup.boolean() as Yup.BooleanSchema<boolean>,
  array: <S extends Yup.AnySchema, C extends AnyObject>(type: S) =>
    Yup.array(type) as Yup.ArraySchema<S, C, Yup.TypeOf<S>[]>,
  ofType<V>() {
    return Yup.mixed<V>() as MixedSchema<V>;
  },
  enum: <Enum extends string, Null extends null = never>(
    enumObj: { [K: string]: Enum },
    _nullValue?: Null,
  ) => {
    return YupDefined.string()
      .nullable()
      .oneOf([
        ...Object.values(enumObj),
        null, // Pass through nulls to any .required in the chain
      ]) as Yup.StringSchema<Enum | Null, Record<string, any>, Enum>;
  },

  union: <
    TagKey extends string,
    Enum extends string,
    RawShapes extends Record<Enum, Record<string, Yup.AnySchema | undefined>>,
  >(
    tagKey: TagKey,
    enumObj: { [K: string]: Enum },
    schemas: RawShapes,
  ) => {
    const schema = Object.entries(schemas).reduce(
      (acc, [tag, schemaForTag]) =>
        acc.when(`.${tagKey}`, {
          is: tag,
          then: s =>
            s.concat(
              Yup.object(
                transformObjectAsKvps(schemaForTag, kvps =>
                  kvps.filter(([, v]) => v !== undefined),
                ) as Record<string, Yup.AnySchema>,
              ),
            ),
        }),
      Yup.object({ [tagKey]: YupDefined.enum(enumObj) }),
    );
    return schema as UnionSchema<TagKey, Enum, RawShapes>;
  },

  conditional: <
    TagKey extends string,
    Shape extends Record<string, Yup.AnySchema>,
  >(
    tagKey: TagKey,
    shape: Shape,
  ) => {
    const schema = Yup.object({ [tagKey]: YupDefined.boolean() }).when(
      `.${tagKey}`,
      {
        is: true,
        then: s => s.concat(Yup.object(shape)),
      },
    );
    return schema as MixedSchema<unknown> as MixedSchema<
      TypeOfShape<Shape> & Record<TagKey, boolean>,
      AnyObject,
      | (AssertsShape<Shape> & Record<TagKey, true>)
      | (TypeOfShape<Shape> & Record<TagKey, false>)
    >;
  },
};

export const usZipCodeRegex = /^\d{5}(-\d{4})?$/;

export function getZipCodeValidator() {
  return YupDefined.string()
    .nullable()
    .trim()
    .matches(usZipCodeRegex, "Please enter a valid zip code.");
}
